import { useClerk, useOrganization, useOrganizationList, useUser } from "@clerk/react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  LockIcon,
  PlusIcon,
  UploadIcon,
  UserPlusIcon,
  UsersIcon,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
} from "react";

import { hasClerkPublishableKey } from "../../cloud/publicConfig";
import { recommendToolkits } from "../../composioConnect";
import { fetchCompanyBrief, type MorrowCompanySituation } from "../../morrowCompanyBrief";
import {
  companyLogoFileFromUrl,
  completeOnboarding,
  getOnboardingProfile,
  isInviteEmail,
  normalizeCompanyWebsite,
  parseCompanyLogoUrl,
  readCompanyLogoFile,
  writeOnboardingProfile,
  type MorrowAccountType,
  type MorrowInvitedPerson,
  type MorrowQuestionnaire,
} from "../../onboardingStore";
import { MorrowMascot } from "../brand/MorrowMascot";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import { ConnectAppCard } from "../chat/ConnectAppCard";
import { appCardForToolkit } from "../chat/morrowLandingCatalog";
import {
  Autocomplete,
  AutocompleteCollection,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
  AutocompletePopup,
} from "../ui/autocomplete";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { createOrgMember, type OrgClerkRole } from "../../orgMembers";
import { useEntitlements, startTrial } from "../../morrowEntitlements";
import { openUpgradeModal } from "../../upgradeModal";
import { isSeatLimitError } from "../workspace/MorrowTeamPage";
import {
  MorrowPersonCard,
  MorrowPersonRemoveButton,
  personRoleLine,
} from "../workspace/MorrowPersonCard";
import {
  MORROW_USER_INDUSTRY_META,
  MORROW_USER_ROLE_META,
} from "../workspace/morrowUserProfileStore";
import { useI18n, type Translator } from "~/i18n";
import { cn } from "../../lib/utils";
import {
  isOnboardingStepPending,
  nextPendingStep,
  onboardingStepProgress,
  organizationLogoUrl,
  pendingOnboardingSteps,
  stepsForAccountType,
  type OnboardingStep,
} from "./MorrowOnboarding.logic";

/**
 * First-run onboarding. The flow branches on account type:
 *   individual: account-type -> connect apps -> done
 *   company:    account-type -> company (Clerk org) -> connect apps -> invite -> done
 * Individual is the fast path so the wizard clears quickly on new chats.
 */

/**
 * Free plan cap on connected apps during onboarding. Connecting is gated to
 * 1..MAX_FREE_CONNECTIONS: the "start chatting" button stays disabled until at
 * least one app is connected, and a fourth connect attempt is blocked with an
 * upgrade nudge (real billing lands separately).
 */
const MAX_FREE_CONNECTIONS = 3;

interface MorrowOnboardingProps {
  readonly connectedToolkits: ReadonlySet<string>;
  readonly connectingToolkits: ReadonlySet<string>;
  readonly disconnectingToolkits: ReadonlySet<string>;
  readonly onConnect: (toolkit: string) => void;
  readonly onDisconnect: (toolkit: string) => void;
}

export function MorrowOnboarding({
  connectedToolkits,
  connectingToolkits,
  disconnectingToolkits,
  onConnect,
  onDisconnect,
}: MorrowOnboardingProps) {
  const { t } = useI18n();
  const clerkEnabled = hasClerkPublishableKey();
  const { organization, membership, isLoaded: orgLoaded } = useOrganization();

  // Init from the persisted profile so a page reload mid-onboarding (e.g. coming
  // back from an OAuth app-connect) RESUMES where the user left off instead of
  // bouncing back to the Individual/Company picker. The effect below snaps `step`
  // to the first still-pending step once the account type is known.
  const [accountType, setAccountType] = useState<MorrowAccountType | null>(
    () => getOnboardingProfile().accountType,
  );
  const [step, setStep] = useState<OnboardingStep>("accountType");
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const [companyName, setCompanyName] = useState(() => getOnboardingProfile().companyName);
  const [companyWebsite, setCompanyWebsite] = useState(() => getOnboardingProfile().companyWebsite);
  const [companyLogoUrl, setCompanyLogoUrl] = useState(() => getOnboardingProfile().companyLogoUrl);
  const [companyBrief, setCompanyBrief] = useState(() => getOnboardingProfile().companyBrief);
  const [companySituation, setCompanySituation] = useState(
    () => getOnboardingProfile().companySituation,
  );
  const [invitePeople, setInvitePeople] = useState<MorrowInvitedPerson[]>([]);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [questionnaire, setQuestionnaire] = useState<MorrowQuestionnaire>(
    () => getOnboardingProfile().questionnaire,
  );
  // Set true when a fourth connect attempt is blocked, to show the upgrade nudge.
  const [connectCapNotice, setConnectCapNotice] = useState(false);
  // Navigation history: the steps actually visited, in order. Back retraces this
  // stack instead of the pending-flow order — completing a step (e.g. creating an
  // org) drops it from `flow`, but the user still expects Back to return there.
  // `hasNavigated` latches once the user drives the wizard, disabling the initial
  // auto-skip (resume/late-Clerk-load) so a manual Back can't be bounced forward.
  const [history, setHistory] = useState<OnboardingStep[]>([]);
  const [hasNavigated, setHasNavigated] = useState(false);

  // Read the real, already-known state instead of asking for it. A Clerk
  // Organization means this is a company account (no Individual/Company prompt,
  // and the company step is already done); ≥1 member beyond the owner means
  // teammates were already invited; ≥1 connected app means Connect is done.
  const ready = !clerkEnabled || orgLoaded;
  const detectedCompany = clerkEnabled && Boolean(organization);
  // Invited member = in an org they didn't create (role isn't org:admin). They
  // skip the company-setup + invite steps straight to the personal steps.
  const isInvitedMember =
    detectedCompany && Boolean(membership) && membership?.role !== "org:admin";
  const membersCount = organization?.membersCount ?? 0;
  const clerkLogoUrl = organizationLogoUrl(organization);
  const resolvedCompanyName = companyName.trim() || organization?.name?.trim() || "";
  const resolvedCompanyWebsite = companyWebsite.trim();
  const resolvedCompanyLogoUrl = companyLogoUrl.trim() || clerkLogoUrl;
  const resolvedCompanyBrief = companyBrief.trim();
  const resolvedCompanySituation = companySituation;

  // A detected Clerk org overrides any manual choice.
  const effectiveAccountType: MorrowAccountType | null = detectedCompany ? "company" : accountType;

  // Whether a given step still has work left. Anything already satisfied by real
  // state is dropped from the flow so the user only sees what's actually pending.
  // The questionnaire counts as answered once a role is captured; that's the
  // signal that drives whether the "profile" step is still pending.
  const questionnaireAnswered = Boolean(questionnaire.role?.trim());
  const pendingInput = useMemo(
    () => ({
      detectedCompany,
      questionnaireAnswered,
      connectedCount: connectedToolkits.size,
      membersCount,
      isInvitedMember,
    }),
    [detectedCompany, questionnaireAnswered, connectedToolkits.size, membersCount, isInvitedMember],
  );
  const isStepPending = useCallback(
    (s: OnboardingStep) => isOnboardingStepPending(s, pendingInput),
    [pendingInput],
  );

  const path = stepsForAccountType(effectiveAccountType);
  const flow = useMemo(
    () => pendingOnboardingSteps(effectiveAccountType, pendingInput),
    [effectiveAccountType, pendingInput],
  );
  const { current: stepNumber, total: stepTotal } = onboardingStepProgress(
    step,
    effectiveAccountType,
  );
  const pathIndex = Math.max(0, path.indexOf(step));
  // Forward = the first still-pending step after the current one in the canonical
  // path (see nextPendingStep); back follows the visited-history stack.
  const nextStep = nextPendingStep(effectiveAccountType, pendingInput, step);
  const canGoBack = history.length > 0;

  const goForward = useCallback(
    (next: OnboardingStep) => {
      setHasNavigated(true);
      setHistory((prev) => [...prev, step]);
      setDirection("forward");
      setStep(next);
    },
    [step],
  );
  const goBack = useCallback(() => {
    if (history.length === 0) return;
    const previous = history[history.length - 1];
    if (!previous) return;
    setHasNavigated(true);
    setDirection("back");
    setStep(previous);
    setHistory((prev) => prev.slice(0, -1));
  }, [history]);

  const finishIndividual = useCallback(() => {
    // Reverse trial: an individual lands straight into a 7-day, no-credit-card trial of
    // the paid tier — "try before you pay". Fire-and-forget + idempotent server-side, so
    // it never blocks finishing onboarding (the whole point is to not stop anyone), and a
    // returning user who already has a plan/trial is a no-op.
    void startTrial();
    completeOnboarding({ accountType: "individual" });
  }, []);

  const finishCompany = useCallback(
    (people: ReadonlyArray<MorrowInvitedPerson>, name: string) => {
      completeOnboarding({
        accountType: "company",
        companyName: name.trim(),
        companyWebsite: resolvedCompanyWebsite,
        companyLogoUrl: resolvedCompanyLogoUrl,
        companyBrief: resolvedCompanyBrief,
        companySituation: resolvedCompanySituation,
        invitedPeople: people,
      });
    },
    [
      resolvedCompanyWebsite,
      resolvedCompanyLogoUrl,
      resolvedCompanyBrief,
      resolvedCompanySituation,
    ],
  );

  const chooseAccountType = useCallback(
    (choice: MorrowAccountType) => {
      // Jump straight to the first step that still has work for this choice — or
      // finish outright if nothing's left (e.g. apps already connected).
      const rest = stepsForAccountType(choice).filter(
        (s) => s !== "accountType" && isStepPending(s),
      );
      // Persist the choice immediately so a reload mid-flow (e.g. returning from
      // an OAuth connect) resumes with this account type instead of resetting.
      writeOnboardingProfile({ accountType: choice });
      const first = rest[0];
      if (!first) {
        if (choice === "company") finishCompany(invitePeople, resolvedCompanyName);
        else finishIndividual();
        return;
      }
      setAccountType(choice);
      setHasNavigated(true);
      // Seed history with the account-type step so Back from the first real step
      // returns here to re-pick, rather than vanishing.
      setHistory(["accountType"]);
      setDirection("forward");
      setStep(first);
    },
    [isStepPending, invitePeople, resolvedCompanyName, finishCompany, finishIndividual],
  );

  // Everything already done (e.g. an existing company with apps + members):
  // complete silently so the wizard never appears.
  useEffect(() => {
    if (!ready || flow.length > 0) return;
    if (detectedCompany) {
      completeOnboarding({
        accountType: "company",
        companyName: resolvedCompanyName,
        companyWebsite: resolvedCompanyWebsite,
        companyLogoUrl: resolvedCompanyLogoUrl,
        companyBrief: resolvedCompanyBrief,
        companySituation: resolvedCompanySituation,
      });
    } else {
      completeOnboarding({ accountType: accountType ?? "individual" });
    }
  }, [
    ready,
    flow.length,
    detectedCompany,
    accountType,
    resolvedCompanyName,
    resolvedCompanyWebsite,
    resolvedCompanyLogoUrl,
    resolvedCompanyBrief,
    resolvedCompanySituation,
  ]);

  // If the current step got skipped (e.g. Clerk finished loading and revealed an
  // org), snap to the first remaining step.
  useEffect(() => {
    if (!ready || flow.length === 0 || hasNavigated) return;
    if (!flow.includes(step)) {
      const first = flow[0];
      if (first) setStep(first);
    }
  }, [ready, flow, step, hasNavigated]);

  // Append a fully-described teammate (email required; the rest optional). The
  // draft form owns its own field + validation state; the parent only keeps the
  // committed list so completion can send the invites and persist rich fields.
  const addInvitePerson = (person: MorrowInvitedPerson) => {
    setInvitePeople((current) =>
      current.some((existing) => existing.email === person.email) ? current : [...current, person],
    );
  };
  const removeInvitePerson = (email: string) => {
    setInvitePeople((current) => current.filter((item) => item.email !== email));
  };

  // Five recommended toolkit slugs from the questionnaire answers — this replaces
  // the old static five-app grid so the connect step matches the user's work.
  const recommendedToolkits = useMemo(
    () => recommendToolkits({ role: questionnaire.role, industry: questionnaire.industry }),
    [questionnaire.role, questionnaire.industry],
  );

  // Connect gate: at least one app must be connected before finishing, and no
  // more than MAX_FREE_CONNECTIONS may be connected on the free plan.
  const connectedCount = connectedToolkits.size;
  const handleConnectGated = useCallback(
    (toolkit: string) => {
      if (connectedToolkits.has(toolkit)) return;
      if (connectedToolkits.size >= MAX_FREE_CONNECTIONS) {
        setConnectCapNotice(true);
        return;
      }
      setConnectCapNotice(false);
      onConnect(toolkit);
    },
    [connectedToolkits, onConnect],
  );

  const persistQuestionnaire = useCallback((answers: MorrowQuestionnaire) => {
    setQuestionnaire(answers);
    writeOnboardingProfile({ questionnaire: answers });
  }, []);

  const heading = stepHeading(t, step);

  // Wait for Clerk before rendering (so we never flash the account-type prompt to
  // a user who's actually in an org), and render nothing once every step is done.
  if (!ready || flow.length === 0 || !path.includes(step)) return null;

  return (
    <div className="pointer-events-auto mx-auto w-full max-w-md">
      <div className="relative">
        {/* Ambient glow behind the panel — the same raised-surface language as the
            signed-out trial CTA card, so onboarding reads as one continuous flow. */}
        <div
          aria-hidden
          className="absolute -inset-px -z-10 rounded-[28px] bg-gradient-to-b from-primary/25 via-primary/5 to-transparent blur-[3px]"
        />
        <div className="flex flex-col gap-7 rounded-3xl border border-border/60 bg-gradient-to-b from-card/80 to-card/40 p-6 shadow-xl shadow-primary/5 backdrop-blur-md sm:p-7">
          {/* Header: mascot + step badge on the left, segmented progress on the right. */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="relative shrink-0">
                <div
                  aria-hidden
                  className="absolute inset-0 -z-10 scale-150 rounded-full bg-primary/25 blur-xl"
                />
                <MorrowMascot className="size-10 sm:size-11" />
              </div>
              <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-primary">
                {t("onboarding.stepOf", { current: stepNumber, total: stepTotal })}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5"
              aria-label={t("onboarding.stepOf", { current: stepNumber, total: stepTotal })}
            >
              {path.map((item, index) => (
                <span
                  key={item}
                  className={cn(
                    "h-1.5 rounded-full transition-[width,background-color] duration-200",
                    index === pathIndex
                      ? "w-6 bg-primary"
                      : index < pathIndex
                        ? "w-1.5 bg-primary/50"
                        : "w-1.5 bg-border",
                  )}
                />
              ))}
            </div>
          </div>

          <div
            key={step}
            className={cn(
              "flex w-full flex-col gap-5",
              direction === "forward" ? "morrow-onboarding-forward" : "morrow-onboarding-back",
            )}
          >
            <div className="flex flex-col gap-1.5">
              <h2 className="text-2xl font-semibold tracking-[-0.02em] text-foreground">
                {heading.title}
              </h2>
              <p className="text-balance text-muted-foreground text-sm">{heading.subtitle}</p>
            </div>

            {step === "accountType" ? <AccountTypeStep onChoose={chooseAccountType} /> : null}

            {step === "company" ? (
              <CompanyStep
                companyName={companyName}
                companyWebsite={companyWebsite}
                companyLogoUrl={resolvedCompanyLogoUrl}
                teamSize={questionnaire.teamSize ?? ""}
                canGoBack={canGoBack}
                onBack={goBack}
                onCompanyName={setCompanyName}
                onCompanyWebsite={setCompanyWebsite}
                onCompanyLogoUrl={setCompanyLogoUrl}
                onContinue={(name, website, logoUrl, lookup, teamSize) => {
                  setCompanyName(name);
                  setCompanyWebsite(website);
                  setCompanyLogoUrl(logoUrl);
                  setCompanyBrief(lookup.brief);
                  setCompanySituation(lookup.situation);
                  writeOnboardingProfile({
                    accountType: "company",
                    companyName: name,
                    companyWebsite: website,
                    companyLogoUrl: logoUrl,
                    companyBrief: lookup.brief,
                    companySituation: lookup.situation,
                  });
                  // Team size is org-level; persist it onto the questionnaire so
                  // it's saved and read exactly as before, just from this step.
                  const { teamSize: _prev, ...rest } = questionnaire;
                  persistQuestionnaire(teamSize ? { ...rest, teamSize } : rest);
                  if (nextStep) goForward(nextStep);
                  else finishCompany(invitePeople, name);
                }}
              />
            ) : null}

            {step === "profile" ? (
              <ProfileStep
                initial={questionnaire}
                canGoBack={canGoBack}
                onBack={goBack}
                onContinue={(answers) => {
                  persistQuestionnaire(answers);
                  if (nextStep) goForward(nextStep);
                  else if (effectiveAccountType === "company")
                    finishCompany(invitePeople, resolvedCompanyName);
                  else finishIndividual();
                }}
              />
            ) : null}

            {step === "connect" ? (
              <div className="flex w-full flex-col gap-4">
                <div className="flex flex-col gap-2">
                  {recommendedToolkits.map((slug) => (
                    <ConnectAppCard
                      key={slug}
                      model={appCardForToolkit(slug)}
                      connected={connectedToolkits.has(slug)}
                      connecting={connectingToolkits.has(slug)}
                      disconnecting={disconnectingToolkits.has(slug)}
                      pulsing={false}
                      onConnect={handleConnectGated}
                      onDisconnect={onDisconnect}
                    />
                  ))}
                </div>
                <div className="flex justify-end px-1">
                  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    {t("onboarding.connectedCount", {
                      connected: connectedCount,
                      total: MAX_FREE_CONNECTIONS,
                    })}
                  </span>
                </div>
                {connectCapNotice ? (
                  <p className="flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
                    <LockIcon className="size-3.5 shrink-0" aria-hidden="true" />
                    {t("onboarding.connectCap", { max: MAX_FREE_CONNECTIONS })}
                  </p>
                ) : null}
                <div className="flex items-center justify-between gap-2">
                  {canGoBack ? (
                    <Button onClick={goBack} size="sm" variant="ghost">
                      <ArrowLeftIcon />
                      {t("common.back")}
                    </Button>
                  ) : (
                    <span />
                  )}
                  {nextStep ? (
                    // Never hard-stop onboarding: proceeding is always allowed. With nothing
                    // connected the button reads "Skip" (they can set up apps later from settings);
                    // once they've connected something it reads "Continue".
                    <Button onClick={() => goForward(nextStep)} size="sm">
                      {connectedCount === 0 ? t("common.skip") : t("common.continue")}
                      <ArrowRightIcon />
                    </Button>
                  ) : (
                    <Button
                      onClick={
                        effectiveAccountType === "company"
                          ? () => finishCompany(invitePeople, resolvedCompanyName)
                          : finishIndividual
                      }
                      size="sm"
                    >
                      {connectedCount === 0 ? t("common.skip") : t("onboarding.startChatting")}
                      <ArrowRightIcon />
                    </Button>
                  )}
                </div>
              </div>
            ) : null}

            {step === "invite" ? (
              <InviteStep
                companyName={resolvedCompanyName}
                canGoBack={canGoBack}
                people={invitePeople}
                busy={inviteBusy}
                onAdd={addInvitePerson}
                onRemove={removeInvitePerson}
                onBack={goBack}
                onSkip={() => finishCompany(invitePeople, resolvedCompanyName)}
                onBusyChange={setInviteBusy}
                onFinish={(people) => finishCompany(people, resolvedCompanyName)}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function stepHeading(t: Translator, step: OnboardingStep): { title: string; subtitle: string } {
  switch (step) {
    case "accountType":
      return {
        title: t("onboarding.accountType.title"),
        subtitle: t("onboarding.accountType.subtitle"),
      };
    case "company":
      return {
        title: t("onboarding.company.title"),
        subtitle: t("onboarding.company.subtitle"),
      };
    case "profile":
      return {
        title: t("onboarding.profile.title"),
        subtitle: t("onboarding.profile.subtitle"),
      };
    case "connect":
      return {
        title: t("onboarding.tools.title"),
        subtitle: t("onboarding.tools.subtitle"),
      };
    case "invite":
      return {
        title: t("onboarding.invite.title"),
        subtitle: t("onboarding.invite.subtitle"),
      };
  }
}

function AccountTypeStep({ onChoose }: { readonly onChoose: (choice: MorrowAccountType) => void }) {
  const { t } = useI18n();
  return (
    <div className="grid w-full gap-3 sm:grid-cols-2">
      <AccountTypeCard
        icon={<UserPlusIcon className="size-5" />}
        title={t("onboarding.accountType.individual")}
        hint={t("onboarding.accountType.individualHint")}
        onClick={() => onChoose("individual")}
      />
      <AccountTypeCard
        icon={<UsersIcon className="size-5" />}
        title={t("onboarding.accountType.company")}
        hint={t("onboarding.accountType.companyHint")}
        onClick={() => onChoose("company")}
      />
    </div>
  );
}

function AccountTypeCard({
  icon,
  title,
  hint,
  onClick,
}: {
  readonly icon: ReactNode;
  readonly title: string;
  readonly hint: string;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group relative flex flex-col gap-4 overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-b from-card/70 to-card/25 p-5 text-left shadow-sm backdrop-blur-sm transition-all duration-200",
        "hover:-translate-y-0.5 hover:border-primary/40 hover:from-primary/[0.10] hover:to-card/40 hover:shadow-xl hover:shadow-primary/10",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      {/* Top-edge gradient hairline that warms on hover, echoing the trial CTA card. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent opacity-0 transition-opacity group-hover:opacity-100"
      />
      <div className="flex items-center justify-between">
        <span className="flex size-11 items-center justify-center rounded-xl border border-border/70 bg-background/70 text-primary shadow-sm transition-colors group-hover:border-primary/40 group-hover:bg-primary/10">
          {icon}
        </span>
        <span className="flex size-7 items-center justify-center rounded-full text-muted-foreground/40 transition-all group-hover:bg-primary/10 group-hover:text-primary">
          <ArrowRightIcon
            aria-hidden
            className="size-4 transition-transform group-hover:translate-x-0.5"
          />
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-base font-semibold tracking-[-0.01em] text-foreground">{title}</span>
        <span className="text-xs leading-relaxed text-muted-foreground/85">{hint}</span>
      </div>
    </button>
  );
}

/**
 * Questionnaire step. A few small fields captured right after account type so
 * Morrow can greet the user in context and recommend the right apps to connect.
 * Everyone answers role / industry / goal; the company-only team size is captured
 * earlier on the company step and stored on the same questionnaire.
 * Answers persist to the onboarding store; role + industry are mirrored into
 * Clerk `unsafeMetadata` (best-effort) so they follow the user across devices.
 */
const TEAM_SIZE_OPTIONS = ["1-10", "11-50", "51-200", "200+"] as const;

const PROFILE_ROLE_OPTION_KEYS = [
  "onboarding.profile.roleOption.founder",
  "onboarding.profile.roleOption.ceo",
  "onboarding.profile.roleOption.cto",
  "onboarding.profile.roleOption.engineer",
  "onboarding.profile.roleOption.devops",
  "onboarding.profile.roleOption.product",
  "onboarding.profile.roleOption.designer",
  "onboarding.profile.roleOption.marketer",
  "onboarding.profile.roleOption.sales",
  "onboarding.profile.roleOption.operations",
  "onboarding.profile.roleOption.projectManager",
  "onboarding.profile.roleOption.customerSuccess",
  "onboarding.profile.roleOption.dataAnalyst",
  "onboarding.profile.roleOption.hr",
  "onboarding.profile.roleOption.finance",
  "onboarding.profile.roleOption.legal",
  "onboarding.profile.roleOption.support",
  "onboarding.profile.roleOption.content",
  "onboarding.profile.roleOption.consultant",
  "onboarding.profile.roleOption.freelancer",
  "onboarding.profile.roleOption.student",
] as const;

const PROFILE_INDUSTRY_OPTION_KEYS = [
  "onboarding.profile.industryOption.saas",
  "onboarding.profile.industryOption.startup",
  "onboarding.profile.industryOption.technology",
  "onboarding.profile.industryOption.agency",
  "onboarding.profile.industryOption.marketing",
  "onboarding.profile.industryOption.ecommerce",
  "onboarding.profile.industryOption.retail",
  "onboarding.profile.industryOption.consulting",
  "onboarding.profile.industryOption.finance",
  "onboarding.profile.industryOption.insurance",
  "onboarding.profile.industryOption.healthcare",
  "onboarding.profile.industryOption.education",
  "onboarding.profile.industryOption.media",
  "onboarding.profile.industryOption.gaming",
  "onboarding.profile.industryOption.realEstate",
  "onboarding.profile.industryOption.manufacturing",
  "onboarding.profile.industryOption.logistics",
  "onboarding.profile.industryOption.hospitality",
  "onboarding.profile.industryOption.foodBeverage",
  "onboarding.profile.industryOption.travel",
  "onboarding.profile.industryOption.construction",
  "onboarding.profile.industryOption.energy",
  "onboarding.profile.industryOption.fashion",
  "onboarding.profile.industryOption.legal",
  "onboarding.profile.industryOption.nonprofit",
  "onboarding.profile.industryOption.government",
] as const;

function ProfileSuggestionField({
  autoFocus = false,
  label,
  name,
  options,
  placeholder,
  value,
  onChange,
}: {
  readonly autoFocus?: boolean;
  readonly label: string;
  readonly name: string;
  readonly options: readonly string[];
  readonly placeholder: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1.5 text-left">
      <span className="text-xs font-medium text-foreground">{label}</span>
      <Autocomplete items={options} value={value} onValueChange={onChange}>
        <AutocompleteInput
          autoFocus={autoFocus}
          name={name}
          placeholder={placeholder}
          showTrigger
          size="lg"
        />
        <AutocompletePopup
          className="dropdown-glass max-h-[14rem] shadow-[0_16px_40px_-18px_rgb(0_0_0/55%)] dark:shadow-[0_18px_44px_-18px_rgb(0_0_0/80%)]"
          collisionAvoidance={{ side: "none", align: "none", fallbackAxisSide: "none" }}
        >
          <AutocompleteList>
            <AutocompleteCollection>
              {(option) => (
                <AutocompleteItem key={option} value={option}>
                  {option}
                </AutocompleteItem>
              )}
            </AutocompleteCollection>
          </AutocompleteList>
        </AutocompletePopup>
      </Autocomplete>
    </label>
  );
}

function ProfileStep({
  initial,
  canGoBack,
  onBack,
  onContinue,
}: {
  readonly initial: MorrowQuestionnaire;
  readonly canGoBack: boolean;
  readonly onBack: () => void;
  readonly onContinue: (answers: MorrowQuestionnaire) => void;
}) {
  const { t } = useI18n();
  const { user } = useUser();
  const [role, setRole] = useState(initial.role ?? "");
  const [industry, setIndustry] = useState(initial.industry ?? "");
  const [goal, setGoal] = useState(initial.goal ?? "");
  const roleOptions = useMemo(() => PROFILE_ROLE_OPTION_KEYS.map((key) => t(key)), [t]);
  const industryOptions = useMemo(() => PROFILE_INDUSTRY_OPTION_KEYS.map((key) => t(key)), [t]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const trimmedRole = role.trim();
    if (!trimmedRole) return;
    const trimmedIndustry = industry.trim();
    const trimmedGoal = goal.trim();
    const answers: MorrowQuestionnaire = {
      role: trimmedRole,
      ...(trimmedIndustry ? { industry: trimmedIndustry } : {}),
      // Team size is captured on the company step; preserve whatever was set there.
      ...(initial.teamSize ? { teamSize: initial.teamSize } : {}),
      ...(trimmedGoal ? { goal: trimmedGoal } : {}),
    };

    // Mirror role + industry into Clerk so they survive a re-install / new device.
    // Best-effort: the local store is already the source of truth for onboarding.
    if (hasClerkPublishableKey() && user) {
      void user
        .update({
          unsafeMetadata: {
            ...user.unsafeMetadata,
            [MORROW_USER_ROLE_META]: trimmedRole,
            [MORROW_USER_INDUSTRY_META]: trimmedIndustry,
          },
        })
        .catch(() => {
          // Non-fatal — the questionnaire is saved locally regardless.
        });
    }

    onContinue(answers);
  };

  return (
    <form className="flex w-full flex-col gap-3" onSubmit={handleSubmit}>
      <ProfileSuggestionField
        autoFocus
        label={t("onboarding.profile.role")}
        name="role"
        options={roleOptions}
        placeholder={t("onboarding.profile.rolePlaceholder")}
        value={role}
        onChange={setRole}
      />

      <ProfileSuggestionField
        label={t("onboarding.profile.industry")}
        name="industry"
        options={industryOptions}
        placeholder={t("onboarding.profile.industryPlaceholder")}
        value={industry}
        onChange={setIndustry}
      />

      <label className="flex flex-col gap-1.5 text-left">
        <span className="text-xs font-medium text-foreground">
          {t("onboarding.profile.goal")}{" "}
          <span className="font-normal text-muted-foreground">({t("common.optional")})</span>
        </span>
        <Input
          nativeInput
          size="lg"
          name="goal"
          placeholder={t("onboarding.profile.goalPlaceholder")}
          value={goal}
          onChange={(event) => setGoal(event.currentTarget.value)}
        />
      </label>

      <div className="flex items-center justify-between gap-2">
        {canGoBack ? (
          <Button onClick={onBack} size="sm" type="button" variant="ghost">
            <ArrowLeftIcon />
            {t("common.back")}
          </Button>
        ) : (
          <span />
        )}
        <Button disabled={!role.trim()} size="sm" type="submit">
          {t("common.continue")}
          <ArrowRightIcon />
        </Button>
      </div>
    </form>
  );
}

/**
 * Company step. Wires Clerk Organizations for real: if the user already belongs
 * to an org we prefill from it; otherwise we create one on continue and set it
 * active. Logo is stored as a data URL so it survives step changes even when
 * Clerk upload is skipped. When Organizations aren't enabled in the Clerk
 * dashboard, createOrganization throws — we catch it, surface a friendly note,
 * and still let onboarding proceed (name, site, and logo persist locally).
 */
function CompanyStep({
  companyName,
  companyWebsite,
  companyLogoUrl,
  teamSize: initialTeamSize,
  canGoBack,
  onCompanyName,
  onCompanyWebsite,
  onCompanyLogoUrl,
  onBack,
  onContinue,
}: {
  readonly companyName: string;
  readonly companyWebsite: string;
  readonly companyLogoUrl: string;
  readonly teamSize: string;
  readonly canGoBack: boolean;
  readonly onCompanyName: (value: string) => void;
  readonly onCompanyWebsite: (value: string) => void;
  readonly onCompanyLogoUrl: (value: string) => void;
  readonly onBack: () => void;
  readonly onContinue: (
    name: string,
    website: string,
    logoUrl: string,
    lookup: { brief: string; situation: MorrowCompanySituation | "" },
    teamSize: string,
  ) => void;
}) {
  const { t } = useI18n();
  const clerkEnabled = hasClerkPublishableKey();
  const { organization } = useOrganization();
  const orgList = useOrganizationList();
  const [name, setName] = useState(companyName || organization?.name || "");
  const [website, setWebsite] = useState(companyWebsite);
  const [teamSize, setTeamSize] = useState(initialTeamSize);
  const [logoUrl, setLogoUrl] = useState(companyLogoUrl || organizationLogoUrl(organization));
  const [websiteError, setWebsiteError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const logoFileRef = useRef<File | null>(null);

  useEffect(() => {
    if (!logoUrl && companyLogoUrl) setLogoUrl(companyLogoUrl);
  }, [companyLogoUrl, logoUrl]);

  const pickLogo = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0] ?? null;
    event.currentTarget.value = "";
    if (!file) return;
    logoFileRef.current = file;
    const preview = URL.createObjectURL(file);
    setLogoUrl(preview);
    void readCompanyLogoFile(file).then((dataUrl) => {
      if (!dataUrl) return;
      setLogoUrl(dataUrl);
      onCompanyLogoUrl(dataUrl);
      window.setTimeout(() => URL.revokeObjectURL(preview), 0);
    });
  };

  const handleContinue = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    const normalizedWebsite = normalizeCompanyWebsite(website);
    if (normalizedWebsite === null) {
      setWebsiteError(t("onboarding.websiteError"));
      return;
    }
    const persistedLogo =
      parseCompanyLogoUrl(logoUrl) ||
      (logoFileRef.current ? ((await readCompanyLogoFile(logoFileRef.current)) ?? "") : "");
    onCompanyName(trimmed);
    onCompanyWebsite(normalizedWebsite);
    onCompanyLogoUrl(persistedLogo);

    setBusy(true);
    setNote(normalizedWebsite ? t("onboarding.lookingUpCompany") : null);
    const lookedUp = normalizedWebsite ? await fetchCompanyBrief(normalizedWebsite) : null;
    const lookup = {
      brief: lookedUp?.brief ?? "",
      situation: lookedUp?.situation ?? ("" as const),
    };

    if (!clerkEnabled) {
      onContinue(trimmed, normalizedWebsite, persistedLogo, lookup, teamSize);
      setBusy(false);
      setNote(null);
      return;
    }

    setNote(null);
    try {
      // Already in an org: reuse it (optionally apply a logo), don't create.
      let activeOrg = organization ?? null;
      if (!activeOrg && orgList.isLoaded && orgList.createOrganization) {
        const created = await orgList.createOrganization({ name: trimmed });
        activeOrg = created;
        await orgList.setActive?.({ organization: created.id });
      }
      let nextLogoUrl = persistedLogo;
      const uploadSource = logoFileRef.current
        ? logoFileRef.current
        : persistedLogo.startsWith("data:image/")
          ? await companyLogoFileFromUrl(persistedLogo)
          : null;
      if (activeOrg && uploadSource) {
        try {
          await activeOrg.setLogo({ file: uploadSource });
          const reloaded = await activeOrg.reload();
          const uploaded = organizationLogoUrl(reloaded);
          if (uploaded) nextLogoUrl = uploaded;
        } catch {
          // Logo is optional — keep the local preview even if Clerk upload fails.
        }
      }
      onContinue(
        activeOrg?.name?.trim() || trimmed,
        normalizedWebsite,
        nextLogoUrl,
        lookup,
        teamSize,
      );
    } catch {
      // Organizations likely not enabled in the Clerk dashboard. Don't hard-block.
      setNote(t("onboarding.orgDisabled"));
      onContinue(trimmed, normalizedWebsite, persistedLogo, lookup, teamSize);
    } finally {
      setBusy(false);
    }
  };

  const hasExistingOrg = Boolean(organization);
  const hasLogo = Boolean(logoUrl);

  return (
    <form className="flex w-full flex-col gap-3" onSubmit={handleContinue}>
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-stretch gap-x-3 gap-y-1.5">
        <span className="text-xs font-medium leading-4 text-foreground">
          {t("onboarding.orgLogo")}
        </span>
        <span className="text-xs font-medium leading-4 text-foreground">
          {t("onboarding.companyName")}
        </span>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          aria-label={hasLogo ? t("onboarding.orgLogoChange") : t("onboarding.orgLogoAdd")}
          className={cn(
            "relative size-9.5 shrink-0 overflow-hidden rounded-lg border border-dashed border-input bg-background text-muted-foreground shadow-xs/5 transition sm:size-8.5",
            "flex items-center justify-center hover:border-primary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          {hasLogo ? (
            <img alt="" className="absolute inset-0 size-full object-cover" src={logoUrl} />
          ) : (
            <UploadIcon className="size-4" />
          )}
        </button>
        <Input
          autoFocus
          nativeInput
          size="lg"
          name="companyName"
          placeholder="Acme"
          aria-label={t("onboarding.companyName")}
          value={name}
          onChange={(event) => setName(event.currentTarget.value)}
        />
      </div>
      <input ref={fileRef} accept="image/*" className="hidden" type="file" onChange={pickLogo} />

      <label className="flex flex-col gap-1.5 text-left">
        <span className="text-xs font-medium text-foreground">
          {t("onboarding.website")}{" "}
          <span className="font-normal text-muted-foreground">({t("common.optional")})</span>
        </span>
        <Input
          nativeInput
          size="lg"
          name="companyWebsite"
          placeholder={t("onboarding.websitePlaceholder")}
          value={website}
          aria-invalid={websiteError ? true : undefined}
          onChange={(event) => {
            setWebsite(event.currentTarget.value);
            if (websiteError) setWebsiteError(null);
          }}
        />
      </label>
      {websiteError ? <p className="text-xs text-error-foreground">{websiteError}</p> : null}

      <label className="flex flex-col gap-1.5 text-left">
        <span className="text-xs font-medium text-foreground">
          {t("onboarding.profile.teamSize")}
        </span>
        <select
          name="teamSize"
          value={teamSize}
          onChange={(event) => setTeamSize(event.currentTarget.value)}
          className={cn(
            "h-10 rounded-lg border border-input bg-background px-3 text-sm text-foreground shadow-xs/5 transition",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          <option value="">{t("onboarding.profile.teamSizePlaceholder")}</option>
          {TEAM_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </label>

      {hasExistingOrg ? (
        <p className="text-xs text-muted-foreground">
          {t("onboarding.orgExisting", { name: organization?.name ?? "" })}
        </p>
      ) : null}
      {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}

      <div className="flex items-center justify-between gap-2">
        {canGoBack ? (
          <Button onClick={onBack} size="sm" type="button" variant="ghost">
            <ArrowLeftIcon />
            {t("common.back")}
          </Button>
        ) : (
          <span />
        )}
        <Button disabled={!name.trim() || busy} size="sm" type="submit">
          {t("common.continue")}
          <ArrowRightIcon />
        </Button>
      </div>
    </form>
  );
}

type InviteStepProps = {
  readonly companyName: string;
  readonly canGoBack: boolean;
  readonly people: ReadonlyArray<MorrowInvitedPerson>;
  readonly busy: boolean;
  readonly onAdd: (person: MorrowInvitedPerson) => void;
  readonly onRemove: (email: string) => void;
  readonly onBack: () => void;
  readonly onSkip: () => void;
  readonly onBusyChange: (busy: boolean) => void;
  readonly onFinish: (people: ReadonlyArray<MorrowInvitedPerson>) => void;
};

function InviteStep(props: InviteStepProps) {
  if (hasClerkPublishableKey()) {
    return <ClerkInviteStep {...props} />;
  }

  // No Clerk: there's no org to invite into or persist rich fields against, so we
  // just keep the described teammates locally (finishCompany persists them to the
  // onboarding store) — today's behavior, richer data.
  return (
    <InviteForm
      canGoBack={props.canGoBack}
      people={props.people}
      busy={false}
      signedIn
      onAdd={props.onAdd}
      onRemove={props.onRemove}
      onBack={props.onBack}
      onSkip={props.onSkip}
      onContinue={() => props.onFinish(props.people)}
    />
  );
}

function ClerkInviteStep({
  companyName,
  canGoBack,
  people,
  busy,
  onAdd,
  onRemove,
  onBack,
  onSkip,
  onBusyChange,
  onFinish,
}: InviteStepProps) {
  const { t } = useI18n();
  const { isLoaded, isSignedIn, openSignIn } = useMorrowClerkAuth();
  const clerk = useClerk();
  const { organization } = useOrganization();
  const entitlements = useEntitlements();
  // Business seat cap for this org's plan (null on individual / unknown).
  const seatLimit = entitlements.limit("seats");

  const sendInvites = async () => {
    if (people.length === 0) {
      onFinish(people);
      return;
    }
    onBusyChange(true);
    try {
      // The active org should already exist from the company step; create as a
      // fallback if it doesn't (e.g. it errored earlier), then invite per-email.
      // If org creation throws (Organizations disabled) the outer catch still
      // completes onboarding — the rich fields stay in the local store only.
      let org = organization;
      if (!org) {
        org = await clerk.createOrganization({
          name: companyName.trim() || t("onboarding.teamFallback"),
        });
        await clerk.setActive({ organization: org.id });
      }
      for (const person of people) {
        const clerkRole: OrgClerkRole = person.clerkRole ?? "org:member";
        try {
          // The backend owns BOTH the authoritative Clerk invite (with an app
          // redirectUrl so the email opens Morrow, not Clerk's hosted portal) AND
          // the rich domain member row. Only reached once an active org exists.
          await createOrgMember({
            email: person.email,
            role: clerkRole,
            title: person.title,
            functionRole: person.functionRole ?? "",
            description: person.description ?? "",
            defaultSensitivity: "normal",
          });
        } catch (err) {
          // Out of seats → surface the upgrade card and stop inviting further.
          if (isSeatLimitError(err)) {
            onBusyChange(false);
            openUpgradeModal({ capability: "seats" });
            return;
          }
          // Other per-email failures shouldn't block the rest or completion.
        }
      }
      onFinish(people);
    } catch {
      onFinish(people);
    } finally {
      onBusyChange(false);
    }
  };

  return (
    <InviteForm
      canGoBack={canGoBack}
      people={people}
      busy={busy}
      signedIn={isLoaded && isSignedIn}
      onAdd={onAdd}
      onRemove={onRemove}
      onBack={onBack}
      onSkip={onSkip}
      onSignIn={openSignIn}
      onContinue={() => void sendInvites()}
      seatLimit={seatLimit}
    />
  );
}

/**
 * The rich per-teammate draft form. Email is required; title, "what they do",
 * an about blurb, and the Clerk access level are all optional so an email-only
 * (or fully skipped) invite still works exactly as before. Committed teammates
 * are listed below as removable cards.
 */
function InviteForm({
  canGoBack,
  people,
  busy,
  signedIn,
  onAdd,
  onRemove,
  onBack,
  onSkip,
  onSignIn,
  onContinue,
  seatLimit,
}: {
  readonly canGoBack: boolean;
  readonly people: ReadonlyArray<MorrowInvitedPerson>;
  readonly busy: boolean;
  readonly signedIn: boolean;
  readonly onAdd: (person: MorrowInvitedPerson) => void;
  readonly onRemove: (email: string) => void;
  readonly onBack: () => void;
  readonly onSkip: () => void;
  readonly onSignIn?: () => void;
  readonly onContinue: () => void;
  /** Plan seat cap (business). null = unknown/unlimited. */
  readonly seatLimit?: number | null;
}) {
  const { t } = useI18n();
  // The owner already holds one seat, so teammates you can attach = seats − 1.
  const invitableTotal = seatLimit != null ? Math.max(0, seatLimit - 1) : null;
  const invitableLeft = invitableTotal != null ? Math.max(0, invitableTotal - people.length) : null;
  const seatsFull = invitableLeft != null && invitableLeft <= 0;
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [functionRole, setFunctionRole] = useState("");
  const [description, setDescription] = useState("");
  const [clerkRole, setClerkRole] = useState<OrgClerkRole>("org:member");
  const [error, setError] = useState<string | null>(null);

  const resetDraft = () => {
    setEmail("");
    setTitle("");
    setFunctionRole("");
    setDescription("");
    setClerkRole("org:member");
  };

  const handleAdd = (event: FormEvent) => {
    event.preventDefault();
    const normalized = email.trim().toLowerCase();
    if (!normalized) return;
    if (!isInviteEmail(normalized)) {
      setError(t("onboarding.inviteError"));
      return;
    }
    // Already added — clear the draft, no duplicate row.
    if (people.some((person) => person.email === normalized)) {
      resetDraft();
      setError(null);
      return;
    }
    // Seat cap: the owner + invited teammates can't exceed the plan's seats.
    // Open the upgrade modal (manage/upgrade from there) instead of a dead error.
    if (seatsFull) {
      openUpgradeModal({ capability: "seats" });
      return;
    }
    const fn = functionRole.trim();
    const desc = description.trim();
    onAdd({
      email: normalized,
      title: title.trim(),
      // Mirror the function into the legacy `role` field so the local Team-page
      // pending list keeps rendering it; the DB write uses `functionRole`.
      role: fn,
      ...(fn ? { functionRole: fn } : {}),
      ...(desc ? { description: desc } : {}),
      clerkRole,
    });
    resetDraft();
    setError(null);
  };

  return (
    <div className="flex w-full flex-col gap-4">
      {invitableTotal != null ? (
        <p className="text-center text-xs text-muted-foreground">
          Your plan includes{" "}
          <span className="font-semibold text-foreground">{seatLimit} seats</span> — you can attach{" "}
          <span className="font-semibold text-foreground">{invitableTotal}</span> teammate
          {invitableTotal === 1 ? "" : "s"}
          {invitableLeft != null && invitableLeft < invitableTotal
            ? ` (${invitableLeft} left)`
            : ""}
          .
        </p>
      ) : null}
      <form
        className="flex w-full flex-col gap-2.5 rounded-2xl border border-border/60 bg-card/40 p-3"
        onSubmit={handleAdd}
      >
        <label className="flex flex-col gap-1.5 text-left">
          <span className="text-xs font-medium text-foreground">
            {t("onboarding.invitePerson.email")}
          </span>
          <Input
            autoFocus
            nativeInput
            size="lg"
            name="inviteEmail"
            placeholder="teammate@acme.com"
            type="email"
            value={email}
            aria-invalid={error ? true : undefined}
            onChange={(event) => {
              setEmail(event.currentTarget.value);
              if (error) setError(null);
            }}
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1.5 text-left">
            <span className="text-xs font-medium text-foreground">
              {t("onboarding.invitePerson.title")}{" "}
              <span className="font-normal text-muted-foreground">({t("common.optional")})</span>
            </span>
            <Input
              nativeInput
              size="lg"
              name="inviteTitle"
              placeholder={t("onboarding.invitePerson.titlePlaceholder")}
              value={title}
              onChange={(event) => setTitle(event.currentTarget.value)}
            />
          </label>
          <label className="flex flex-col gap-1.5 text-left">
            <span className="text-xs font-medium text-foreground">
              {t("onboarding.invitePerson.function")}{" "}
              <span className="font-normal text-muted-foreground">({t("common.optional")})</span>
            </span>
            <Input
              nativeInput
              size="lg"
              name="inviteFunction"
              placeholder={t("onboarding.invitePerson.functionPlaceholder")}
              value={functionRole}
              onChange={(event) => setFunctionRole(event.currentTarget.value)}
            />
          </label>
        </div>
        <label className="flex flex-col gap-1.5 text-left">
          <span className="text-xs font-medium text-foreground">
            {t("onboarding.invitePerson.description")}{" "}
            <span className="font-normal text-muted-foreground">({t("common.optional")})</span>
          </span>
          <Textarea
            size="sm"
            name="inviteDescription"
            placeholder={t("onboarding.invitePerson.descriptionPlaceholder")}
            rows={2}
            value={description}
            onChange={(event) => setDescription(event.currentTarget.value)}
          />
        </label>
        <div className="flex items-end justify-between gap-2">
          <label className="flex min-w-0 flex-1 flex-col gap-1.5 text-left">
            <span className="text-xs font-medium text-foreground">
              {t("onboarding.invitePerson.access")}
            </span>
            <select
              name="inviteAccess"
              value={clerkRole}
              onChange={(event) => {
                const value = event.currentTarget.value;
                if (value === "org:admin" || value === "org:member") setClerkRole(value);
              }}
              className={cn(
                "h-10 rounded-lg border border-input bg-background px-3 text-sm text-foreground shadow-xs/5 transition",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              )}
            >
              <option value="org:member">{t("team.roleMember")}</option>
              <option value="org:admin">{t("team.roleAdmin")}</option>
            </select>
          </label>
          <Button size="sm" type="submit" variant="outline">
            <PlusIcon />
            {t("onboarding.invitePerson.add")}
          </Button>
        </div>
      </form>
      {error ? <p className="text-xs text-error-foreground">{error}</p> : null}
      {people.length > 0 ? (
        <ul className="flex w-full flex-col gap-2">
          {people.map((person) => {
            const fn = person.functionRole ?? person.role;
            return (
              <li key={person.email} className="w-full">
                <MorrowPersonCard
                  layout="row"
                  name={person.email}
                  title={person.title}
                  role={fn}
                  description={
                    person.description?.trim() ||
                    personRoleLine(person.title, fn) ||
                    t("onboarding.pendingInvite")
                  }
                  action={
                    <MorrowPersonRemoveButton
                      label={t("onboarding.removeEmail", { email: person.email })}
                      onClick={() => onRemove(person.email)}
                    />
                  }
                />
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-center text-muted-foreground text-xs">{t("onboarding.addEmailsHint")}</p>
      )}
      {!signedIn ? (
        <p className="text-center text-muted-foreground text-xs">
          {t("onboarding.signInToInvite")}
        </p>
      ) : null}
      <div className="flex items-center justify-between gap-2">
        {canGoBack ? (
          <Button onClick={onBack} size="sm" variant="ghost">
            <ArrowLeftIcon />
            {t("common.back")}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <Button onClick={onSkip} size="sm" variant="ghost">
            {t("common.skip")}
          </Button>
          {!signedIn && onSignIn ? (
            <Button onClick={onSignIn} size="sm">
              {t("common.signIn")}
            </Button>
          ) : (
            <Button disabled={busy} onClick={onContinue} size="sm">
              {people.length > 0 ? t("onboarding.sendAndStart") : t("onboarding.startChatting")}
              <ArrowRightIcon />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
